import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Mail, 
  Copy, 
  Download, 
  Search,
  Users,
  Bell,
  Shield,
  BookOpen,
  CheckCircle,
  Filter
} from 'lucide-react';

const AdminEmailsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterAlerts, setFilterAlerts] = useState('all');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/admin-login');
      return;
    }
    
    if (user.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    
    fetchEmails();
  }, [user, authLoading, navigate]);

  const fetchEmails = async () => {
    try {
      const response = await api.get('/api/admin/emails');
      setEmails(response.data.emails);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Erro ao carregar lista de emails');
    } finally {
      setLoading(false);
    }
  };

  // Filter emails based on search and filters
  const filteredEmails = emails.filter(email => {
    const matchesSearch = 
      email.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || email.role === filterRole;
    
    const matchesAlerts = 
      filterAlerts === 'all' ||
      (filterAlerts === 'enabled' && email.weekly_alerts) ||
      (filterAlerts === 'disabled' && !email.weekly_alerts);
    
    return matchesSearch && matchesRole && matchesAlerts;
  });

  // Handle select all
  useEffect(() => {
    if (selectAll) {
      setSelectedEmails(filteredEmails.map(e => e.email));
    } else {
      setSelectedEmails([]);
    }
  }, [selectAll, filteredEmails.length]);

  const toggleEmailSelection = (email) => {
    setSelectedEmails(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text);
    toast.success(message || 'Copiado!');
  };

  const copyAllEmails = () => {
    const emailList = filteredEmails.map(e => e.email).join(', ');
    copyToClipboard(emailList, `${filteredEmails.length} emails copiados!`);
  };

  const copySelectedEmails = () => {
    if (selectedEmails.length === 0) {
      toast.error('Selecione pelo menos um email');
      return;
    }
    copyToClipboard(selectedEmails.join(', '), `${selectedEmails.length} emails copiados!`);
  };

  const copyEmailsForMailchimp = () => {
    // Format for Mailchimp (one email per line)
    const emailList = filteredEmails.map(e => e.email).join('\n');
    copyToClipboard(emailList, `${filteredEmails.length} emails copiados (formato Mailchimp)!`);
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Nome', 'Papel', 'Alertas Semanais', 'Data de Cadastro', 'Áreas de Interesse'];
    const rows = filteredEmails.map(e => [
      e.email,
      e.name || '',
      e.role,
      e.weekly_alerts ? 'Sim' : 'Não',
      e.created_at ? new Date(e.created_at).toLocaleDateString('pt-BR') : '',
      (e.areas || []).join('; ')
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `emails_queropublicar_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV exportado com sucesso!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getRoleBadge = (role) => {
    const badges = {
      'admin': <Badge className="bg-red-100 text-red-700">Admin</Badge>,
      'editor': <Badge className="bg-blue-100 text-blue-700">Editor</Badge>,
      'researcher': <Badge className="bg-gray-100 text-gray-700">Pesquisador</Badge>
    };
    return badges[role] || badges['researcher'];
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
                  <Mail className="h-6 w-6 text-[#002FA7]" />
                  Lista de Emails
                </h1>
                <p className="text-sm text-gray-500">Gerenciar lista para newsletter e novidades</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold text-[#002FA7]">{stats.total || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Bell className="h-4 w-4" />
              <span className="text-sm">Com Alertas</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.with_weekly_alerts || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <BookOpen className="h-4 w-4" />
              <span className="text-sm">Pesquisadores</span>
            </div>
            <p className="text-2xl font-bold text-gray-700">{stats.researchers || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <BookOpen className="h-4 w-4" />
              <span className="text-sm">Editores</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.editors || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Shield className="h-4 w-4" />
              <span className="text-sm">Admins</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.admins || 0}</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:w-auto">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por email ou nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="border border-gray-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">Todos os papéis</option>
                <option value="researcher">Pesquisadores</option>
                <option value="editor">Editores</option>
                <option value="admin">Admins</option>
              </select>
              <select
                value={filterAlerts}
                onChange={(e) => setFilterAlerts(e.target.value)}
                className="border border-gray-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">Todos</option>
                <option value="enabled">Com alertas ativos</option>
                <option value="disabled">Sem alertas</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copySelectedEmails}
                disabled={selectedEmails.length === 0}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copiar Selecionados ({selectedEmails.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyAllEmails}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copiar Todos ({filteredEmails.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyEmailsForMailchimp}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <Mail className="h-4 w-4 mr-1" />
                Formato Mailchimp
              </Button>
              <Button
                size="sm"
                onClick={exportToCSV}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-1" />
                Exportar CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Email List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => setSelectAll(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Papel</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Alertas</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Cadastro</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEmails.map((email, index) => (
                  <tr 
                    key={email.email} 
                    className={`hover:bg-gray-50 ${selectedEmails.includes(email.email) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedEmails.includes(email.email)}
                        onChange={() => toggleEmailSelection(email.email)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{email.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{email.name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getRoleBadge(email.role)}
                    </td>
                    <td className="px-4 py-3">
                      {email.weekly_alerts ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="h-4 w-4" />
                          Ativo
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">{formatDate(email.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(email.email, 'Email copiado!')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredEmails.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhum email encontrado</p>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2">Dicas para Newsletter</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Use o botão "Formato Mailchimp" para copiar emails em formato compatível com ferramentas de email marketing</li>
            <li>• Exporte para CSV para importar em plataformas como Mailchimp, SendGrid ou ConvertKit</li>
            <li>• Usuários com "Alertas Ativos" já optaram por receber comunicações</li>
            <li>• Filtre por papel para enviar comunicações segmentadas</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailsPage;
